import { createRouter, createWebHistory } from "vue-router";
import { isAuthenticated, sessionReady, setSessionError } from "../stores/session.js";

/**
 * Real routes.
 *
 * The previous app rendered the entire authenticated product at a single URL —
 * it contained one <a> element, so nothing could be linked, bookmarked or
 * shared, and browser Back did nothing (docs/audit/05-ui-ux.md, G5.4).
 *
 * `requiresAuth` is a convenience, not a security control: the API enforces
 * access on every request. A guard only decides what to render.
 */
const routes = [
  // --- public ---
  { path: "/", name: "overview", component: () => import("../views/public/OverviewView.vue"), meta: { title: "Overview" } },
  { path: "/project-types", name: "project-types", component: () => import("../views/public/ProjectTypesView.vue"), meta: { title: "Project types" } },
  { path: "/feasibility", name: "feasibility", component: () => import("../views/public/FeasibilityView.vue"), meta: { title: "Feasibility" } },
  { path: "/investors", name: "investors", component: () => import("../views/public/InvestorsView.vue"), meta: { title: "Investors" } },
  { path: "/volunteers", name: "volunteers", component: () => import("../views/public/VolunteersView.vue"), meta: { title: "Volunteers" } },
  { path: "/contact", name: "contact", component: () => import("../views/public/ContactView.vue"), meta: { title: "Contact" } },
  { path: "/login", name: "login", component: () => import("../views/public/LoginView.vue"), meta: { title: "Sign in", guestOnly: true } },
  // Second step of sign-in. Both hold the challenge in memory only, so a reload
  // correctly sends the visitor back to the password form.
  { path: "/sign-in/verify", name: "mfa-verify", component: () => import("../views/public/MfaChallengeView.vue"), meta: { title: "Verification code" } },
  { path: "/sign-in/set-up", name: "mfa-enrol", component: () => import("../views/public/MfaEnrolView.vue"), meta: { title: "Set up two-step verification" } },
  { path: "/forgot-password", name: "forgot-password", component: () => import("../views/public/ForgotPasswordView.vue"), meta: { title: "Reset your password" } },
  { path: "/reset-password", name: "reset-password", component: () => import("../views/public/ResetPasswordView.vue"), meta: { title: "Choose a new password" } },
  { path: "/register", name: "register", component: () => import("../views/public/RegisterUserView.vue"), meta: { title: "Create an account" } },
  { path: "/register-project", name: "register-project", component: () => import("../views/public/RegisterProjectView.vue"), meta: { title: "Register a project" } },

  // --- workspace ---
  {
    path: "/workspace",
    component: () => import("../views/workspace/WorkspaceLayout.vue"),
    meta: { requiresAuth: true },
    children: [
      { path: "", redirect: { name: "dashboard" } },
      { path: "dashboard", name: "dashboard", component: () => import("../views/workspace/DashboardView.vue"), meta: { title: "Dashboard" } },
      { path: "projects", name: "projects", component: () => import("../views/workspace/ProjectsView.vue"), meta: { title: "Projects" } },
      { path: "projects/new", name: "project-new", component: () => import("../views/workspace/ProjectCreateView.vue"), meta: { title: "New project" } },
      // Every record is addressable — a reviewer can send a colleague a link.
      { path: "projects/:id/edit", name: "project-edit", component: () => import("../views/workspace/ProjectEditView.vue"), props: true, meta: { title: "Edit project" } },
      { path: "projects/:id", name: "project", component: () => import("../views/workspace/ProjectDetailView.vue"), props: true, meta: { title: "Project" } },
      { path: "eligibility", name: "eligibility", component: () => import("../views/workspace/EligibilityView.vue"), meta: { title: "Eligibility screening" } },
      { path: "processor", name: "processor", component: () => import("../views/workspace/ProcessorView.vue"), meta: { title: "Processor" } },
      { path: "studies", name: "studies", component: () => import("../views/workspace/StudiesView.vue"), meta: { title: "Feasibility studies" } },
      { path: "studies/new", name: "study-new", component: () => import("../views/workspace/StudyCreateView.vue"), meta: { title: "Request a study" } },
      { path: "studies/:id", name: "study", component: () => import("../views/workspace/StudyDetailView.vue"), props: true, meta: { title: "Feasibility study" } },
      { path: "portal", name: "portal", component: () => import("../views/workspace/BuyerPortalView.vue"), meta: { title: "Buyer portal" } },
      { path: "review", name: "review", component: () => import("../views/workspace/ReviewQueueView.vue"), meta: { title: "Review queue" } },
      { path: "notifications", name: "notifications", component: () => import("../views/workspace/NotificationsView.vue"), meta: { title: "Notifications" } },
      { path: "messages", name: "messages", component: () => import("../views/workspace/MessagesView.vue"), meta: { title: "Messages" } },
      { path: "people", name: "people", component: () => import("../views/workspace/UsersView.vue"), meta: { title: "People and access" } },
      { path: "reports", name: "reports", component: () => import("../views/workspace/ReportsView.vue"), meta: { title: "Reports" } },
      { path: "settings", name: "settings", component: () => import("../views/workspace/SettingsView.vue"), meta: { title: "System settings" } },
      { path: "approvals", name: "approvals", component: () => import("../views/workspace/ApprovalsView.vue"), meta: { title: "Approvals" } }
    ]
  },

  { path: "/:pathMatch(.*)*", name: "not-found", component: () => import("../views/public/NotFoundView.vue"), meta: { title: "Page not found" } }
];

export const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, from, saved) {
    if (saved) return saved;
    if (to.hash) return { el: to.hash, behavior: "smooth" };
    return { top: 0 };
  }
});

router.beforeEach(async (to) => {
  // A token restored from storage is only a claim until the API resolves it into
  // an identity. Wait for that to settle, so a deep link neither renders a
  // workspace for a dead token nor bounces a real session to the sign-in page.
  await sessionReady();

  if (to.meta.requiresAuth && !isAuthenticated.value) {
    setSessionError("Please sign in to open your workspace.");
    return { name: "login", query: { next: to.fullPath } };
  }
  if (to.meta.guestOnly && isAuthenticated.value) {
    return { name: "dashboard" };
  }
  return true;
});

router.afterEach((to) => {
  const title = to.meta.title ? `${to.meta.title} · ScionCarbon Project Center` : "ScionCarbon Project Center";
  document.title = title;
});
